import { Alert, Box, Button, Card, CardActions, CardContent, Chip, Divider, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { SendRounded } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PanelSegment from '../../components/PanelSegment';
import PageTitle from '../../components/PageTitle';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { Securables } from '../../Enums';
import { ProducerAdminCompany, ProducerMembershipState, ProducerSystemAdminCompanyCommand } from '../../models/producer';

const money = (cents?: number | null) => cents === null || cents === undefined
    ? 'Not set'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);

const ProducerSystemAdminPage = () => {
    const history = useHistory();
    const { producerCompanyId } = useParams<{ producerCompanyId?: string }>();
    const { hasRole } = useAccessInfo();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(false);
    const [companies, setCompanies] = useState<ProducerAdminCompany[]>([]);
    const [company, setCompany] = useState<ProducerAdminCompany | null>(null);
    const [companyName, setCompanyName] = useState('');
    const [adminFirstName, setAdminFirstName] = useState('');
    const [adminLastName, setAdminLastName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [rateCents, setRateCents] = useState('');
    const [effectiveDate, setEffectiveDate] = useState('');
    const [resultMessage, setResultMessage] = useState('');
    const canManage = hasRole(Securables.synergist_admin, true);

    const load = useCallback(async () => {
        if (!canManage) return;
        try {
            setBusy(true);
            const client = new ProducerPortalClient();
            if (producerCompanyId) setCompany(await client.GetProducerCompany(producerCompanyId));
            else setCompanies(await client.GetProducerCompanies());
        } catch (error) {
            setError(error, 'Could not load Producer administration.');
        } finally {
            setBusy(false);
        }
    }, [canManage, producerCompanyId, setError]);

    useEffect(() => { void load(); }, [load]);

    const createProducer = async () => {
        if (!companyName.trim() || !adminEmail.trim()) return;
        try {
            setBusy(true);
            const command: ProducerSystemAdminCompanyCommand = {
                CompanyName: companyName.trim(), InitialAdminFirstName: adminFirstName.trim(), InitialAdminLastName: adminLastName.trim(), InitialAdminEmail: adminEmail.trim(),
            };
            const claim = await new ProducerPortalClient().AddProducer(command);
            setResultMessage(`${claim.ProducerCompanyName} was created and its secure invitation was sent.`);
            setCompanyName(''); setAdminFirstName(''); setAdminLastName(''); setAdminEmail('');
            await load();
        } catch (error) {
            setError(error, 'Could not create this Producer company.');
        } finally {
            setBusy(false);
        }
    };

    const parsedRateCents = useMemo(() => /^\d+$/.test(rateCents) ? Number(rateCents) : Number.NaN, [rateCents]);
    const validFutureRate = Number.isSafeInteger(parsedRateCents) && parsedRateCents >= 0 && parsedRateCents <= 1500
        && Boolean(effectiveDate) && new Date(effectiveDate).getTime() > Date.now();

    const addRate = async () => {
        if (!company || !validFutureRate) return;
        try {
            setBusy(true);
            await new ProducerPortalClient().AddProducerRate(company.Id, { RateCents: parsedRateCents, EffectiveDate: new Date(effectiveDate).toISOString() });
            setRateCents(''); setEffectiveDate(''); setResultMessage('The future rate was added.');
            await load();
        } catch (error) {
            setError(error, 'Could not add this Producer rate.');
        } finally {
            setBusy(false);
        }
    };

    const setState = async (state: ProducerMembershipState) => {
        if (!company || state === company.Status) return;
        try {
            setBusy(true);
            await new ProducerPortalClient().SetProducerCompanyState(company.Id, state);
            await load();
        } catch (error) {
            setError(error, 'Could not update the Producer company status.');
        } finally {
            setBusy(false);
        }
    };

    if (!canManage) return <Alert severity="warning">Only Synergist admins can use this page.</Alert>;

    if (producerCompanyId) {
        const rates = company?.Rates || [];
        const now = Date.now();
        return <Box><ErrorDisplay /><BusySpinner busy={busy}><PanelSegment
            titleContent={<PageTitle title={company?.Name || 'Producer company'} />}
            toolbar={<Button onClick={() => history.push('/SystemAdministration/Producers')}>Back to Producers</Button>}
        ><Box sx={{ p: 2 }}>
            {resultMessage && <Alert severity="success" sx={{ mb: 2 }}>{resultMessage}</Alert>}
            {company && <Stack spacing={2}>
                <Card variant="outlined"><CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                        <Chip label={`Status: ${company.Status}`} color={company.Status === 'Active' ? 'success' : 'warning'} />
                        <Chip label={`Payout readiness: ${company.PayoutReadiness}`} color={company.PayoutReadiness === 'Ready' ? 'success' : 'warning'} />
                        <Chip label={company.IsOnboardingComplete ? 'Onboarding complete' : 'Onboarding incomplete'} />
                        <Typography>Current rate: {money(company.CurrentRateCents)} per seat</Typography>
                    </Stack>
                    <TextField select label="Company status" value={company.Status} onChange={event => void setState(event.target.value as ProducerMembershipState)} sx={{ mt: 2, minWidth: 220 }}>
                        {(['Active', 'Suspended', 'Terminated'] as ProducerMembershipState[]).map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                    </TextField>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                    <Typography variant="h6">Set a future rate</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Enter whole cents to preserve the server’s exact-money contract. Rates are immutable after creation.</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField label="Rate per seat (cents)" value={rateCents} onChange={event => setRateCents(event.target.value)} inputProps={{ inputMode: 'numeric' }} helperText="0–1500 cents" />
                        <TextField label="Effective date and time" type="datetime-local" value={effectiveDate} onChange={event => setEffectiveDate(event.target.value)} InputLabelProps={{ shrink: true }} />
                        <Button variant="contained" disabled={!validFutureRate} onClick={() => void addRate()}>Add future rate</Button>
                    </Stack>
                </CardContent></Card>
                <Typography variant="h6">Rate history</Typography>
                <TableContainer component={Card}><Table size="small"><TableHead><TableRow><TableCell>Effective</TableCell><TableCell align="right">Rate per seat</TableCell><TableCell>Timing</TableCell></TableRow></TableHead>
                    <TableBody>{rates.length === 0 && <TableRow><TableCell colSpan={3}>No rates have been set.</TableCell></TableRow>}{rates.map(rate => <TableRow key={rate.Id}><TableCell>{new Date(rate.EffectiveDate).toLocaleString()}</TableCell><TableCell align="right">{money(rate.RateCents)}</TableCell><TableCell>{new Date(rate.EffectiveDate).getTime() > now ? 'Upcoming' : rate.RateCents === company.CurrentRateCents ? 'Current' : 'Historical'}</TableCell></TableRow>)}</TableBody>
                </Table></TableContainer>
            </Stack>}
        </Box></PanelSegment></BusySpinner></Box>;
    }

    return <Box><ErrorDisplay /><BusySpinner busy={busy}><PanelSegment titleContent={<PageTitle title="Producers" />} toolbar={<Button onClick={() => history.push('/SystemAdministration/ProducerPayouts')}>Payout runs</Button>}>
        <Box sx={{ p: 2 }}><Stack spacing={2}>
            {resultMessage && <Alert severity="success">{resultMessage}</Alert>}
                <Card variant="outlined"><CardContent><Typography variant="h6" sx={{ mb: 1 }}>Create Producer company</Typography><Stack spacing={1.25}>
                <TextField label="Producer company name" value={companyName} onChange={event => setCompanyName(event.target.value)} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}><TextField label="Initial admin first name" value={adminFirstName} onChange={event => setAdminFirstName(event.target.value)} fullWidth /><TextField label="Initial admin last name" value={adminLastName} onChange={event => setAdminLastName(event.target.value)} fullWidth /></Stack>
                <TextField label="Initial admin email" value={adminEmail} onChange={event => setAdminEmail(event.target.value)} />
            </Stack></CardContent><CardActions><Button startIcon={<SendRounded />} variant="contained" onClick={() => void createProducer()} disabled={!companyName.trim() || !adminEmail.trim()}>Create Producer and send invite</Button></CardActions></Card>
            <Divider />
            <TableContainer component={Card}><Table size="small"><TableHead><TableRow><TableCell>Company</TableCell><TableCell>Status</TableCell><TableCell>Payout readiness</TableCell><TableCell align="right">Current rate</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>{companies.length === 0 && <TableRow><TableCell colSpan={5}>No Producer companies found.</TableCell></TableRow>}{companies.map(item => <TableRow key={item.Id}><TableCell>{item.Name}</TableCell><TableCell>{item.Status}</TableCell><TableCell>{item.PayoutReadiness}</TableCell><TableCell align="right">{money(item.CurrentRateCents)}</TableCell><TableCell><Button onClick={() => history.push(`/SystemAdministration/Producers/${item.Id}`)}>View</Button></TableCell></TableRow>)}</TableBody>
            </Table></TableContainer>
        </Stack></Box>
    </PanelSegment></BusySpinner></Box>;
};

export default ProducerSystemAdminPage;
